import { css as emotionCSS } from '@emotion/react';
import {
  IconButton as MuiIconButton,
  iconButtonClasses as MuiIconButtonClasses,
  IconButtonProps as MuiIconButtonProps,
  svgIconClasses,
  Theme,
} from '@mildang/mui';
import { token } from '@mildang/styled-system/tokens';

export type IconButtonSize = MuiIconButtonProps['size'] | 'xSmall' | 'xLarge' | 'xxSmall';
export type IconButtonColor =
  | MuiIconButtonProps['color']
  | 'white'
  | 'text.tertiary'
  | 'text.primary'
  | 'text.secondary'
  | 'primary.strokeHover'
  | 'primary.contrastText'
  | 'error.dark';

export const iconButtonClasses = MuiIconButtonClasses;

export const iconButtonStyles = {
  xxSmall: emotionCSS`
    width: 20px;
    height: 20px;
    & svg,
    .${svgIconClasses.root} {
      font-size: 12px;
    }
  `,
  xSmall: emotionCSS`
    width: 24px;
    height: 24px;
    & svg,
    .${svgIconClasses.root} {
      font-size: 18px;
    }
  `,
  small: emotionCSS`
    width: 32px;
    height: 32px;
    & svg,
    .${svgIconClasses.root} {
      font-size: 22px;
    }
  `,
  medium: emotionCSS`
    width: 36px;
    height: 36px;
    & svg,
    .${svgIconClasses.root} {
      font-size: 24px;
    }
  `,
  large: emotionCSS`
    width: 42px;
    height: 42px;
    & svg,
    .${svgIconClasses.root} {
      font-size: 28px;
    }
  `,
  xLarge: emotionCSS`
    width: 50px;
    height: 50px;
    & svg,
    .${svgIconClasses.root} {
      font-size: 32px;
    }
  `,
};

const rootStyle = ({
  theme,
  size,
  color,
}: {
  theme: Theme;
  size?: IconButtonSize;
  color?: IconButtonColor;
}) => emotionCSS`
  svg {
    flex-shrink: 0;
  }
  border-radius: 4px;
  &.${iconButtonClasses.sizeSmall} {
    ${iconButtonStyles['small']};
  }
  &.${iconButtonClasses.sizeMedium} {
    ${iconButtonStyles['medium']};
  }
  &.${iconButtonClasses.sizeLarge} {
    ${iconButtonStyles['large']};
  }
  ${
    size === 'xxSmall' &&
    emotionCSS`
    && {
      ${iconButtonStyles['xxSmall']};
    }
  `
  }
  ${
    size === 'xSmall' &&
    emotionCSS`
    && {
      ${iconButtonStyles['xSmall']};
    }
  `
  }
  ${
    size === 'xLarge' &&
    emotionCSS`
    && {
      ${iconButtonStyles['xLarge']};
    }
  `
  }

  &:disabled {
    opacity: 0.38;
  }
  &&:hover {
    background-color: ${token('colors.common.3')};
  }
  ${
    color === 'white' &&
    emotionCSS`
    color: ${token('colors.gray.light.100')};

    &:hover {
      background-color: ${token('colors.gray.dark.a300')} !important;
    }
    &:disabled {
      color: ${token('colors.gray.light.100')} !important;
      opacity: 0.38 !important;
    }
  `
  }
  ${
    color === 'primary' &&
    emotionCSS`
    color: ${token('colors.common.7')};
    &&:hover {
      background-color: ${token('colors.common.5')};
    }
    &:disabled {
      color: ${token('colors.common.7')} !important;
    }
  `
  } 
  ${
    color === 'secondary' &&
    emotionCSS`
    &&:hover {
      background-color: ${theme.palette.secondary.lightActive};
    }
    &:disabled {
      color: ${theme.palette.secondary.main};
      opacity: 0.38;
    }
  `
  } 
  ${
    color === 'error' &&
    emotionCSS`
    &&:hover {
      background-color: ${theme.palette.error.lightActive};
    }
    &:disabled {
      color: ${theme.palette.error.main};
      opacity: 0.38;
    }
  `
  }
  ${
    color === 'text.tertiary' &&
    emotionCSS`
    && {
      & svg,
      .${svgIconClasses.root} {
        color: ${theme.palette.text.tertiary};
      }
    }
    &:disabled {
      opacity: 0.38;
    }
    &&:hover {
      background-color: ${token('colors.common.3')};
    }
  `
  }
    ${
      color === 'text.primary' &&
      emotionCSS`
    && {
      & svg,
      .${svgIconClasses.root} {
        color: ${theme.palette.text.primary};
      }
    }
    &:disabled {
      opacity: 0.38;
    }
    &&:hover {
      background-color: ${token('colors.common.3')};
    }
  `
    }
      ${
        color === 'text.secondary' &&
        emotionCSS`
    && {
      & svg,
      .${svgIconClasses.root} {
        color: ${theme.palette.text.secondary};
      }
    }
    &:disabled {
      opacity: 0.38;
    }
    &&:hover {
      background-color: ${token('colors.common.3')};
    }
  `
      }
   ${
     color === 'primary.strokeHover' &&
     emotionCSS`
    && {
      & svg,
      .${svgIconClasses.root} {
        color: ${theme.palette.primary.strokeHover};
      }
    }
    &:disabled {
      opacity: 0.38;
    }
    &&:hover {
      background-color: ${token('colors.common.3')};
    }
  `
   }
   ${
     color === 'primary.contrastText' &&
     emotionCSS`
    && {
      & svg,
      .${svgIconClasses.root} {
        color: ${theme.palette.primary.contrastText};
      }
    }
    &:disabled {
      opacity: 0.38;
    }
    &&:hover {
      background-color: ${token('colors.common.3')};
    }
  `
   }
     ${
       color === 'error.dark' &&
       emotionCSS`
    && {
      & svg,
      .${svgIconClasses.root} {
        color: ${theme.palette.error.dark};
      }
    }
    &:disabled {
      opacity: 0.38;
    }
    &&:hover {
      background-color: ${token('colors.common.3')};
    }
  `
     }
   
  &:disabled {
    opacity: 0.38;
  }
`;

export interface IconButtonProps extends Omit<MuiIconButtonProps, 'size' | 'color'> {
  size?: IconButtonSize;
  color?: IconButtonColor;
}

const filterNonMuiProp = ({ color, size }: IconButtonProps) => {
  return {
    ...(size === 'xxSmall' || size === 'xSmall' || size === 'xLarge' ? {} : { size }),
    ...(color === 'white' ||
    color === 'text.tertiary' ||
    color === 'text.primary' ||
    color === 'text.secondary' ||
    color === 'primary.strokeHover' ||
    color === 'primary.contrastText' ||
    color === 'error.dark'
      ? {}
      : { color }),
  };
};

const LegacyIconButton = (props: IconButtonProps) => {
  const { size, color, ...others } = props;
  return (
    <MuiIconButton
      css={(theme: any) => rootStyle({ theme, size, color })}
      {...filterNonMuiProp({ size, color })}
      {...others}
    />
  );
};

export default LegacyIconButton;
